/**
 * PreviewCard — VERBATIM port from ~/FrontendFinal/Design/src/components/PreviewCard.tsx.
 *
 * The "Tool Preview" card in the wizard's left panel: a white plate holding a row
 * of integration logos, with the tool name and owner beneath it. The plate inset
 * (8), the four evenly-distributed logo slots, and the flush name/owner lines are
 * reverse-engineered SVG measurement — see the Design repo's header for the full
 * derivation; do not re-measure. Imports nothing, so no repoint.
 *
 * In the reskin `name`/`owner`/`integrations` come from the Vm's live draft; the
 * logos remain labelled placeholders exactly as in the Design (the Design's
 * TODO(dev): the marks are third-party brand assets, not carried in this repo).
 *
 * BACKEND ADDITION, not part of the verbatim port. The Design's TODO(dev) above
 * is now answered: there are no third-party brand assets to carry, so the plate
 * holds GENERATED art instead — `FlowArt`, a deterministic diagram of the flow
 * the builder is actually uploading (see design/FlowArt.tsx). The four dashed
 * `h-logo` stubs are gone with the row they sat in; the connector identity they
 * carried is preserved as `known.connectorCount`, and the connector NAMES are
 * already in the wizard's own text. Every other measured value — the 8px plate
 * inset, the fixed 188 card height, the flush text rhythm — is untouched, and
 * both new props are optional so all four existing call sites still compile.
 */
import { FlowArt, flowArtLabel } from '../../../design/FlowArt';
import type { FlowShapeNode } from '@trailblazer/shared';

/**
 * The plate's own box, and therefore the art's viewBox — in real CSS px.
 *
 * Derived, not measured afresh: the wizard's left track is `--wizard-rule-track`
 * (264), less `--wizard-left-rest` (2.5) and `--wizard-left-gap` (31.5) = a 230px
 * card, less the 8px `--preview-plate-inset` on each side = 214. The height is
 * `--preview-plate-height` (128) exactly. All three left panels that render this
 * card use the same `grid-cols-wizard-body` skeleton, so 214 holds for every one.
 *
 * It only needs to be RIGHT-RATIO, not pixel-exact: `preserveAspectRatio` on the
 * svg letterboxes a mismatch rather than distorting, and `.fa`'s own
 * `background-color` paints any margin. Getting it right is what makes the margin
 * zero, so the art fills the plate.
 */
const ART_W = 214;
const ART_H = 128;

export interface PreviewCardProps {
  /** The tool's own name. */
  name: string;
  /** The owning account. */
  owner: string;
  /** Integration logos; `name` is the placeholder label + accessible name. */
  integrations: { name: string }[];
  /**
   * The flow's structure, if known — an encoded shape or decoded nodes. Absent or
   * unreadable draws the emblem instead of a diagram, which is the honest answer
   * before the manifest has loaded.
   */
  shape?: string | readonly FlowShapeNode[] | null;
  className?: string;
}

export function PreviewCard({
  name,
  owner,
  integrations,
  shape,
  className = '',
}: PreviewCardProps) {
  return (
    <div
      className={[
        'flex h-preview-card w-full min-w-0 flex-col rounded-md bg-accent-subtle',
        // 8 on left/top/right is the measured plate inset; the bottom slack is
        // whatever the fixed 188 height leaves (h-preview-card expresses that).
        'px-preview-plate pt-preview-plate',
        className,
      ].join(' ')}
    >
      {/* The plate. Fixed 128 height; the 8px inset comes from the card's own
          padding, so the plate is a plain full-width child. `overflow-hidden`
          keeps the art's corners inside the r8 radius — the svg is a rectangle
          and would otherwise square them off. */}
      <div className="h-preview-plate w-full overflow-hidden rounded-md bg-surface">
        <FlowArt
          shape={shape}
          name={name}
          width={ART_W}
          height={ART_H}
          known={{ connectorCount: integrations.length }}
          // The one surface where the graphic is MEANINGFUL rather than
          // ornamental: it is the only thing on the page describing the shape of
          // the automation, so it is announced. `flowArtLabel` returns null when
          // there is no structure, which correctly falls back to `aria-hidden` —
          // an emblem says nothing the name beneath it does not already say.
          label={flowArtLabel(shape)}
        />
      </div>

      {/* Name and owner. No gap: the 20px baseline pitch IS the line box
          (--leading-body), so the two lines stack flush. The text is inset 12
          from the card (the plate only 8), so this column carries the 4px
          remainder as px-preview-text-x (the difference of the two insets). */}
      <div className="flex min-w-0 flex-col pt-preview-text px-preview-text-x">
        <p className="truncate text-xs font-medium text-ink">{name}</p>
        <p className="truncate text-xs font-light text-ink">{owner}</p>
      </div>
    </div>
  );
}
